import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  onDelete?: (id: number) => void;
  onAdd?: () => void;
  onDownload?: (report: VptReport) => void;
  onPrint?: (report: VptReport) => void;
  report: VptReport | null;
}

const ReportDialog: React.FC<ReportDialogProps> = ({ 
  open, 
  onClose, 
  onDelete, 
  onDownload,
  onPrint,
  report 
}) => {
  if (!report) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          {report.inventory ? 'Inventory' : 'Stocking'} report
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {onDownload && (
            <IconButton onClick={() => onDownload(report)} color="primary" size="small" sx={{ mr: 1 }}>
              <DownloadIcon />
            </IconButton>
          )}
          {onPrint && (
            <IconButton onClick={() => onPrint(report)} color="primary" size="small" sx={{ mr: 1 }}>
              <PrintIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton onClick={() => onDelete(report.id)} size="small" sx={{ mr: 1, color: 'green' }}>
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.headerBackground,
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: (theme) => theme.palette.border,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {report.storageName}
        </Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" >
            {report.reporterName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(() => {
              const d = new Date(report.date);
              const day = String(d.getDate()).padStart(2, '0');
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const year = d.getFullYear();
              const hours = String(d.getHours()).padStart(2, '0');
              const minutes = String(d.getMinutes()).padStart(2, '0');
              return `${day}.${month}.${year}, ${hours}:${minutes}`;
            })()}
          </Typography>
        </Box>
      </Box>
      <DialogContent sx={{ mt: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Missing products:
          </Typography>
          
          {(report.inventory ? report.inventoryLogs : report.stockingLogs)
            ?.filter((log) => log.amountMissing > 0)
            .map((log, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={(theme) => ({ ...theme.typography.smallTitle })}>
                  <strong>Product Code:</strong> {log.productCode}
                </Typography>
                <Typography sx={(theme) => ({ ...theme.typography.smallTitle })}>
                  <strong>Missing:</strong> {log.amountMissing}
                </Typography>
              </Box>
            ))}

          <Typography variant="h6" gutterBottom>
            Stocked products:
          </Typography>

          {(report.inventory ? report.inventoryLogs : report.stockingLogs)
            ?.filter((log) => log.amountAdded > 0)
            .map((log, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={(theme) => ({ ...theme.typography.smallTitle })}>
                  <strong>Product Code:</strong> {log.productCode}
                </Typography>
                <Typography sx={(theme) => ({ ...theme.typography.smallTitle })}>
                  <strong>Added:</strong> {log.amountAdded}
                </Typography>
              </Box>
            ))}

          {report.imageCount > 0 && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Images:</strong> {report.imageCount}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
